/**
 * Customization Template Admin API — thin wrappers around backend endpoints.
 *
 * Uses getERPClient().call() for mode-aware API calls (JWT or session).
 * Company is resolved server-side from session — NOT passed from frontend.
 *
 * Plan: 117-personalization-admin-ui, Epic 01
 */

import { getERPClient } from "@/lib/erp/client";
import type {
  TemplateListResponse,
  TemplateDetail,
  SaveTemplateInput,
  SaveTemplateResponse,
  DeleteTemplateResponse,
  AddFieldInput,
  AddFieldResponse,
  ValidateForActivationResponse,
  LinkedProductsResponse,
  BundleCustomizationConfig,
  VariantOption,
} from "./customizationAdminTypes";

const API_BASE = "zaviago_backend.customization_admin";
const BUNDLE_API_BASE = "zaviago_backend.customization_bundle";
const BUNDLE_LINKED_API_BASE = "zaviago_backend.customization_bundle_linked";

export const customizationAdminApi = {
  list(search = "", status = "", page = 0, pageLength = 20) {
    const client = getERPClient();
    return client.call<TemplateListResponse>(
      `${API_BASE}.get_template_list`,
      { search, status, page, page_length: pageLength },
    );
  },

  detail(name: string) {
    const client = getERPClient();
    return client.call<TemplateDetail>(
      `${API_BASE}.get_template_detail`,
      { name },
    );
  },

  save(input: SaveTemplateInput) {
    const client = getERPClient();
    return client.call<SaveTemplateResponse>(
      `${API_BASE}.save_template`,
      {
        name: input.name || undefined,
        template_name: input.template_name,
        description: input.description,
        status: input.status,
        fields: JSON.stringify(input.fields),
        rules: JSON.stringify(input.rules),
      },
    );
  },

  delete(name: string) {
    const client = getERPClient();
    return client.call<DeleteTemplateResponse>(
      `${API_BASE}.delete_template`,
      { name },
    );
  },

  addField(input: AddFieldInput) {
    const client = getERPClient();
    return client.call<AddFieldResponse>(
      `${API_BASE}.add_field`,
      {
        template_name: input.template_name,
        label: input.label,
        field_type: input.field_type,
        required: input.required || 0,
        options: input.options || "",
        addon_item: input.addon_item || "",
        max_characters: input.max_characters || 0,
        allowed_file_types: input.allowed_file_types || "",
        max_file_size: input.max_file_size || 5,
      },
    );
  },

  reorderFields(templateName: string, fieldOrder: string[]) {
    const client = getERPClient();
    return client.call<{ success: boolean }>(
      `${API_BASE}.reorder_fields`,
      {
        template_name: templateName,
        field_order: JSON.stringify(fieldOrder),
      },
    );
  },

  validateForActivation(name: string) {
    const client = getERPClient();
    return client.call<ValidateForActivationResponse>(
      `${API_BASE}.validate_for_activation`,
      { name },
    );
  },

  getLinkedProducts(templateName: string) {
    const client = getERPClient();
    return client.call<LinkedProductsResponse>(
      `${API_BASE}.get_linked_products`,
      { template_name: templateName },
    );
  },

  // -- Bundle endpoints (Epic 04) --

  getBundleConfig(itemCode: string) {
    const client = getERPClient();
    return client.call<BundleCustomizationConfig>(
      `${BUNDLE_API_BASE}.get_bundle_customization_config`,
      { item_code: itemCode },
    );
  },

  saveSharedFields(bundleItemRowName: string, sharedFields: string[]) {
    const client = getERPClient();
    return client.call<{ success: boolean }>(
      `${BUNDLE_API_BASE}.save_shared_fields`,
      {
        bundle_item_row_name: bundleItemRowName,
        shared_fields: JSON.stringify(sharedFields),
      },
    );
  },

  getVariantOptions(templateItemCode: string) {
    const client = getERPClient();
    return client.call<VariantOption[]>(
      `${BUNDLE_API_BASE}.get_variant_options`,
      { template_item_code: templateItemCode },
    );
  },

  saveDefaultVariant(bundleItemRowName: string, variantItemCode: string) {
    const client = getERPClient();
    return client.call<{ success: boolean }>(
      `${BUNDLE_API_BASE}.save_default_variant`,
      {
        bundle_item_row_name: bundleItemRowName,
        variant_item_code: variantItemCode,
      },
    );
  },

  saveLinkedAttributes(bundleName: string, attrs: string[]) {
    const client = getERPClient();
    return client.call<{ success: boolean; linked_attributes: string[] }>(
      `${BUNDLE_LINKED_API_BASE}.save_linked_attributes`,
      {
        bundle_name: bundleName,
        linked_attributes: JSON.stringify(attrs),
      },
    );
  },
};
